import React from "react";

import { css, cx } from "@emotion/css";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { PatientSyncMenu } from "@/uiKit/organisms/PatientSync/PatientSyncNavigation.tsx";

export type patientSyncNavigationHeaderProps = {
  className?: string;
};

export const PatientSyncNavigationHeader: React.FC<patientSyncNavigationHeaderProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const navigate = useNavigate();
  const routeMatch = useMatchRoute();
  const isIndexRoute = routeMatch({ to: "/patient-sync" });
  const { t } = useTranslation();

  return (
    <div id="patient-sync-navigation" className={cx(styles.container, className)}>
      <div className={styles.top}>
        {!isIndexRoute && (
          <Icons.arrowLeft
            id="patient-sync-back"
            className={styles.goBack}
            onClick={() => navigate({ to: "/patient-sync" })}
          />
        )}
        <Typography variant="h5">{t("patientSync.title")}</Typography>
      </div>
      <PatientSyncMenu />
    </div>
  );
};

const makeStyles = (theme: Theme) => {
  const patientSyncNavigationHeaderHeight = `calc(2vh + ${theme.typography.h5.fontSize} * ${theme.typography.h5.lineHeight} + ${theme.spacing(16)} + ${theme.spacing(2)} + 2px)`; // top padding + title + title line height + navigation menu + gap + bottom border
  return {
    container: css({
      position: "sticky",
      top: 0,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      background: theme.palette.common.white,
      padding: "2vh 2vw 0 2vw",
      gap: theme.spacing(2),
      borderBottom: `${theme.spacing(1)} solid ${theme.palette.background.default}`,
      zIndex: theme.zIndex.appBar,
      height: patientSyncNavigationHeaderHeight,
    }),
    top: css({
      display: "flex",
      flex: "1 1",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(8),
    }),
    goBack: css({
      color: theme.palette.text.primary,
      width: FontSizes.h5,
      height: FontSizes.h5,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(4),
      },
    }),
  };
};
